import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {ColorFillOption, ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ContextualMenu} from '@Components/contextual-menu';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {DefaultEditingOptionsHeader} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-editing-options-header';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {EditFancyTextOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/edit-fancy-text-option';
import {ItemInteractivitySection} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-section';
import {ItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-shadow-option';
import {FancyTextColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/fancy-text-color-option';
import {FancyTextShapeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/fancy-text-shape-option';
import {FancyTextPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-fancy-text-options/poster-editing-fancy-text-options.types';
import {CustomShadowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/custom-shadow-color-panel';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {FancyTextColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/fancy-text-color-panel';
import {FancyTextFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/fancy-text-font-option';
import {FancyTextStyleOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/fancy-text-style-option';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useisOpenedFancyTextPanelValid} from '@Hooks/poster-editor/useisOpenedFancyTextPanelValid';
import {AnimationPanelType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemIntroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-intro-animation-panel/item-intro-animation-panel';
import {ItemOutroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-outro-animation-panel/item-outro-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';

export function PosterEditingFancyTextOptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(FancyTextPanels.DEFAULT);
  const [selectedColorId, setSelectedColorId] = useState<ColorFillOption>(ColorFillOption.PRIMARY);
  useisOpenedFancyTextPanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel, selectedColorId);

  if (activeItem === null || !activeItem) {
    throw new Error('Active item is undefined in poster editing fancy text options');
  }

  if (!activeItem.isFancyText()) {
    throw new Error(`Expected a fancy text item - got ${activeItem.gitype} instead`);
  }

  const getEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(type: AnimationPanelType): void => {
            setCurrentlyOpenedPanel(type === AnimationPanelType.INTRO_ANIMATION_STYLES ? FancyTextPanels.INTRO_ANIMATION : FancyTextPanels.OUTRO_ANIMATION);
          }}
        />
        <ContextualMenuHeadingOption id="fancy-text-edit-heading" text={window.i18next.t('pmwjs_edit_mask_text')} />
        <EditFancyTextOption />
        <ContextualMenuHeadingOption id="fancy-text-font-heading" text={window.i18next.t('pmwjs_font')} />
        <FancyTextFontOption />
        <FancyTextStyleOption />
        <ContextualMenuHeadingOption id="fancy-text-font-heading" text={window.i18next.t('pmwjs_style')} />
        <FancyTextColorOption
          onClickColorFill={(id: ColorFillOption): void => {
            setSelectedColorId(id);
            setCurrentlyOpenedPanel(FancyTextPanels.COLOR);
          }}
        />
        <FancyTextShapeOption />
        <ItemShadowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(FancyTextPanels.SHADOW);
          }}
        />
        <ItemInteractivitySection />
      </>
    );
  };

  const getDefaultOptions = (): ReactElement<any> => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.POSITION:
        return <ItemPositionContextualOptions />;
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === FancyTextPanels.INTRO_ANIMATION) {
      return window.i18next.t('pmwjs_entrance_animation');
    } else if (currentlyOpenedPanel === FancyTextPanels.OUTRO_ANIMATION) {
      return window.i18next.t('pmwjs_exit_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | null => {
    if (showSubOptions() || currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return (
        <ContextualMenuHeader id="fancytext-contextual-menu-header">
          <ContextualMenuTextHeader
            text={getHeaderText()}
            showBackButton
            onBackButton={(): void => {
              if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
                setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
              }
              setCurrentlyOpenedPanel(FancyTextPanels.DEFAULT);
            }}
          />
        </ContextualMenuHeader>
      );
    }
    return null;
  };

  const getOptions = (): ReactElement<any> => {
    if (showSubOptions()) {
      return getSubOptions();
    }
    return getDefaultOptions();
  };

  const getSubOptions = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case FancyTextPanels.COLOR:
        return <FancyTextColorPanel id={selectedColorId} />;
      case FancyTextPanels.SHADOW:
        return <CustomShadowColorPanel />;
      case FancyTextPanels.INTRO_ANIMATION:
        return <ItemIntroAnimationPanel />;
      case FancyTextPanels.OUTRO_ANIMATION:
        return <ItemOutroAnimationPanel />;
      default:
        throw new Error(`Unhandled sub option panel ${currentlyOpenedPanel} for fancy text`);
    }
  };

  const showSubOptions = (): boolean => {
    return currentlyOpenedPanel !== FancyTextPanels.DEFAULT;
  };

  const getKey = (): string => {
    if (!showSubOptions()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.FANCYTEXT_DEFAULT_POSITION : ContextualMenuContentKeys.FANCYTEXT_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu contentKey={getKey()} id="fancy-text-contextual-options" header={getHeader()} includeLeftRightPadding={!showSubOptions()}>
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentlyOpenedPanel, currentItemHeaderTab]);
}
